import { useEffect, useRef, useState } from 'react'
import {
  useOnAddToBasket,
  isDigitalGiftExperience,
} from '@moonpig/web-shared-products'
import { DepartmentsEnum } from '@moonpig/web-shared-types-graphql/graphqlTypes'
import { ProductMessageItem } from '../../ChatMessages/types'

const BASKET_CONFIRMATION_MS = 1000

export const useConciergeAddToBasket = (
  item: ProductMessageItem,
  onBasketClick?: (item: ProductMessageItem) => void,
  onBasketAddStarted?: (item: ProductMessageItem) => void,
  onBasketAdded?: (item: ProductMessageItem) => void,
  onBasketAddFailed?: (item: ProductMessageItem) => void,
) => {
  const [isBasketLoading, setIsBasketLoading] = useState(false)
  const [isBasketConfirmed, setIsBasketConfirmed] = useState(false)
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (confirmTimerRef.current) {
        clearTimeout(confirmTimerRef.current)
      }
    }
  }, [])

  const { onAddToBasket } = useOnAddToBasket({
    product: item as never,
    index: 0,
    productsLength: 1,
    pageName: 'concierge chat',
    componentName: 'concierge gift tile',
  })

  const startConfirmationTimer = () => {
    if (confirmTimerRef.current) {
      clearTimeout(confirmTimerRef.current)
    }
    confirmTimerRef.current = setTimeout(() => {
      setIsBasketConfirmed(false)
      confirmTimerRef.current = null
    }, BASKET_CONFIRMATION_MS)
  }

  const trackSuccessfulAdd = () => {
    setIsBasketConfirmed(true)
    onBasketAdded?.(item)
    startConfirmationTimer()
  }

  const runAddToBasket = async () => {
    const variant = item.masterVariant

    if (
      isDigitalGiftExperience(item.category?.department as DepartmentsEnum) ||
      !item.category?.department ||
      !variant?.sku
    ) {
      return false
    }

    return onAddToBasket({
      productId: item.id,
      isProductCustomisable: item.customisable ?? false,
      variant,
      quantity: 1,
      selectedAddon: null,
    })
  }

  const handleBasketClick = async (clickedItem: ProductMessageItem) => {
    onBasketAddStarted?.(item)
    setIsBasketLoading(true)
    try {
      const isAdded = await runAddToBasket()

      if (isAdded) {
        trackSuccessfulAdd()
      } else {
        onBasketAddFailed?.(item)
      }
    } catch {
      onBasketAddFailed?.(item)
    } finally {
      setIsBasketLoading(false)
    }

    onBasketClick?.(clickedItem)
  }

  return {
    isBasketLoading,
    isBasketConfirmed,
    handleBasketClick,
  }
}
